import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react'
// import Dompurify from 'dompurify'

export function SearchDropdown({
  options = [], // array of strings OR { label, value }
  placeholder = 'Type at least 2 letters…',
  minChars = 2,
  debounceMs = 120,
  onSelect = () => {},
  className = '',
  maxItems = 8,
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef(null)
  const justChoseRef = useRef(false)
  const listId = useRef(
    `ac-list-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
  ).current

  // Normalize input for a friendlier match (case/diacritics)
  const normalize = (s = '') =>
    s
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()

  // Debounce query for smoother typing
  const [debounced, setDebounced] = useState(query)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), debounceMs)
    return () => clearTimeout(t)
  }, [query, debounceMs])

  // Canonicalize options to {label, value, tag?}
  const canonicalOptions = useMemo(
    () =>
      options.map(o =>
        typeof o === 'string'
          ? {label: o, value: o}
          : {
              label: o.label ?? String(o.value ?? ''),
              value: o.value ?? o.label,
              ...(o.tag && {tag: o.tag}),
            },
      ),
    [options],
  )

  // Filter when we have enough characters
  const filtered = useMemo(() => {
    const q = normalize(debounced)
    if (q.length < minChars) return []
    const qDigits = debounced.replace(/\D/g, '') // just numbers typed

    const results = canonicalOptions.filter(opt => {
      const label = String(opt.label ?? '')
      const nLabel = normalize(label)
      if (nLabel.includes(q)) return true

      // digit-only match
      if (qDigits) {
        const labelDigits = label.replace(/\D/g, '') // "1,000" -> "1000"
        return labelDigits.includes(qDigits)
      }
      return false
    })
    return results.slice(0, maxItems)
  }, [debounced, minChars, canonicalOptions, maxItems])

  // Open/close behavior — skip re-open when triggered by a selection
  useEffect(() => {
    if (justChoseRef.current) {
      justChoseRef.current = false
      return
    }
    setOpen(filtered.length > 0)
    setActiveIndex(filtered.length ? 0 : -1)
  }, [filtered.length])

  // Close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const choose = useCallback(
    opt => {
      justChoseRef.current = true
      setQuery(opt.label)
      setOpen(false)
      setActiveIndex(-1)
      onSelect(opt)
    },
    [onSelect],
  )

  const onKeyDown = e => {
    if (!open && e.key !== 'Tab') setOpen(true)
    if (!open || !filtered.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => (i + 1) % filtered.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => (i - 1 + filtered.length) % filtered.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0) choose(filtered[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Highlight the first occurrence of the query inside the label
  const Highlighted = ({label}) => {
    const q = normalize(debounced)
    if (q.length < minChars) return <>{label}</>
    const nLabel = normalize(label)
    const idx = nLabel.indexOf(q)
    if (idx === -1) return <>{label}</>
    const start = label.slice(0, idx)
    const match = label.slice(idx, idx + debounced.length)
    const end = label.slice(idx + debounced.length)
    return (
      <>
        {start}
        <mark className="rounded bg-yellow-100 px-0.5">{match}</mark>
        {end}
      </>
    )
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <label className="sr-only" htmlFor="ac-input">
        Search
      </label>
      <input
        id="ac-input"
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (normalize(query).length >= minChars && filtered.length)
            setOpen(true)
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={
          activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined
        }
        placeholder={placeholder}
        className="w-full rounded-none border border-gray-300 px-4 py-2 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
      />

      {/* Helper text */}
      {normalize(query).length > 0 && normalize(query).length < minChars && (
        <p className="mt-1 text-xs text-gray-500">
          Type {minChars - normalize(query).length} more character(s)…
        </p>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-2xl border border-gray-200 bg-white shadow-lg"
        >
          {filtered.map((opt, i) => (
            <li
              key={`${opt.value}-${i}`}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={e => e.preventDefault()} // prevent input blur before click
              onClick={() => choose(opt)}
              className={`cursor-pointer px-4 py-2 hover:bg-gray-50 ${
                i === activeIndex ? 'bg-gray-50' : ''
              }`}
            >
              <span className="flex items-center justify-between">
                <span>
                  <Highlighted label={opt.label} />
                </span>
                {opt.tag && (
                  <span
                    className={`ml-2 shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      opt.tag === 'Upgrade'
                        ? 'bg-green-100 text-green-700'
                        : opt.tag === 'Current'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {opt.tag}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
